<!--
SubscriptionTermModal — wraps SubscriptionTermForm.

Publishing is its OWN button, not a side effect of saving. The two acts have
different consequences: saving edits a draft, publishing changes the agreement
every new franchise on that PLAN is asked to sign — in every language — and
retires whatever stood before it.

The version fields and the wording are saved through different endpoints,
because they are different things: a plan has one agreement in force, and the
language variants are translations of it.

Deleting lives in the body, not the footer (Modal.vue reserves the footer for
close and save), and is refused by the server while acceptances point at the
document. The button is disabled from the row's own acceptance count, so the
refusal is visible before the click rather than after it.
-->
<template>
  <modal
    v-if="isVisible"
    elem-id="modal_subscription_term"
    modal-size="modal-lg"
    :title="editingId ? t('Editar termo de assinatura') : t('Nova versão do termo')"
    :show="isVisible"
    :close-label="t('Cancelar')"
    close-test-id="subscription-term-modal-cancel-btn"
    :save-label="isSaving ? t('Salvando') : t('Salvar')"
    save-icon="pi pi-check"
    save-test-id="subscription-term-modal-save-btn"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving"
    data-testid="subscription-term-modal"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <subscription-term-form
      ref="formRef"
      v-model="formData"
      :is-editing="editingId !== null"
      :variants="variants"
      @save="persist"
      @save-variant="persistVariant"
      @validation-failed="handleValidationFailed"
    />

    <div v-if="errorCode" class="term-error" data-testid="subscription-term-modal-error">
      <i class="pi pi-exclamation-triangle"></i>
      {{ humanizeRefusal(errorCode) }}
    </div>

    <div v-if="editingId" class="term-actions">
      <Button
        v-if="!isActive"
        :label="t('Publicar este termo')"
        icon="pi pi-megaphone"
        severity="success"
        size="small"
        :disabled="isSaving"
        data-testid="subscription-term-modal-activate-btn"
        @click="handleActivate"
      />
      <span v-else class="term-live" data-testid="subscription-term-modal-live">
        <i class="pi pi-check-circle"></i>
        {{ t('Este é o termo em vigor para o plano') }} {{ planoNome }}
      </span>

      <Button
        :label="t('Excluir')"
        icon="pi pi-trash"
        severity="danger"
        size="small"
        outlined
        :disabled="isSaving || aceites > 0"
        data-testid="subscription-term-modal-delete-btn"
        @click="handleDelete"
      />
      <small v-if="aceites > 0" class="term-hint" data-testid="subscription-term-modal-delete-blocked">
        {{ t('Não dá para excluir: esta versão já tem') }} {{ aceites }} {{ t('aceite(s) registrado(s).') }}
      </small>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useConfirmAction } from '../../../composables/useConfirmAction'
import { useSubscriptionTerms } from '../../../composables/useSubscriptionTerms'
import SubscriptionTermForm, { type SubscriptionTermFormData } from './SubscriptionTermForm.vue'
import type { SubscriptionTerms_VariantRow } from '../../../contracts/SubscriptionTerms_VariantRow'

const { t } = useI18n()
const { confirmAction } = useConfirmAction()
const { terms, saveTerm, saveVariant, activateTerm, deleteTerm } = useSubscriptionTerms()

const isVisible = ref(false)
const isSaving = ref(false)
const editingId = ref<number | null>(null)
const isActive = ref(false)
const aceites = ref(0)
const errorCode = ref<string | null>(null)
const variants = ref<SubscriptionTerms_VariantRow[]>([])
const planoNome = ref('')

const formRef = ref<InstanceType<typeof SubscriptionTermForm> | null>(null)

const formData = ref<SubscriptionTermFormData>({
  plan_id: 0,
  titulo: '',
  versao: '',
})

/**
 * The server's refusal CODE is shown as a sentence, never replaced by a
 * generic failure. Which rule fired changes what the operator does next.
 */
function humanizeRefusal(code: string): string {
  const map: Record<string, string> = {
    plano_nao_encontrado: t('Plano não encontrado — recarregue a lista.'),
    language_unsupported: t('Idioma não oferecido pelo serviço de traduções.'),
    termo_nao_encontrado: t('Termo não encontrado — recarregue a lista.'),
    termo_possui_aceites: t('Esta versão já foi assinada e não pode ser excluída. Publique a sucessora.'),
    falha_ao_ativar_termo: t('Não foi possível publicar o termo.'),
  }

  return map[code] ?? code
}

function reset(): void {
  editingId.value = null
  isActive.value = false
  aceites.value = 0
  errorCode.value = null
  variants.value = []
  planoNome.value = ''
  formData.value = { plan_id: 0, titulo: '', versao: '' }
}

function closeModal(): void {
  isVisible.value = false
  reset()
}

function openModal(payload?: { id?: number }): void {
  reset()

  const id = payload?.id
  if (id != null) {
    const row = terms.value.find((r) => r.id === id)
    if (row) {
      editingId.value = row.id
      isActive.value = row.ativo ?? false
      aceites.value = row.aceites ?? 0
      variants.value = row.variants ?? []
      planoNome.value = row.plano_nome ?? ''
      formData.value = {
        plan_id: row.plan_id ?? 0,
        titulo: row.titulo ?? '',
        versao: row.versao ?? '',
      }
    }
  }

  isVisible.value = true
  log('SubscriptionTermModal', 'openModal', 'Aberto', { id: editingId.value })
}

/** Modal.vue's save button: delegate to the form so validation runs. */
function handleSave(): void {
  formRef.value?.submit()
}

function handleValidationFailed(): void {
  log('SubscriptionTermModal', 'handleValidationFailed', 'Formulário inválido')
}

/**
 * Save the VERSION. When the form also emits a body, `persistVariant` follows
 * — the two writes are separate endpoints because they are separate acts, and
 * the modal only closes once the wording has landed too.
 */
async function persist(data: SubscriptionTermFormData): Promise<void> {
  isSaving.value = true
  errorCode.value = null

  try {
    const row = await saveTerm({
      ...(editingId.value != null ? { id: editingId.value } : {}),
      plan_id: data.plan_id,
      titulo: data.titulo,
      versao: data.versao,
    })

    const wasCreating = editingId.value == null

    editingId.value = row.id
    isActive.value = row.ativo ?? false
    aceites.value = row.aceites ?? 0
    variants.value = row.variants ?? []
    planoNome.value = row.plano_nome ?? ''
    eventBus.emit('subscription_term.saved', { id: row.id })

    // A brand-new version has no text yet: stay open so the author can write
    // the first language instead of hunting the row down again.
    if (!wasCreating) {
      closeModal()
    }
  } catch (err) {
    errorCode.value = err instanceof Error ? err.message : String(err)
    log('SubscriptionTermModal', 'persist', 'Recusa', errorCode.value)
  } finally {
    isSaving.value = false
  }
}

async function persistVariant(payload: { language: string; conteudo: string }): Promise<void> {
  if (editingId.value == null) return

  isSaving.value = true

  try {
    const row = await saveVariant({
      term_id: editingId.value,
      language: payload.language,
      conteudo: payload.conteudo,
    })

    variants.value = row.variants ?? []
    eventBus.emit('subscription_term.saved', { id: row.id })
  } catch (err) {
    errorCode.value = err instanceof Error ? err.message : String(err)
    log('SubscriptionTermModal', 'persistVariant', 'Recusa', errorCode.value)
  } finally {
    isSaving.value = false
  }
}

function handleActivate(): void {
  if (editingId.value == null) return

  confirmAction({
    message: t('Publicar esta versão? Ela passa a ser o acordo de toda nova assinatura deste plano, em todos os idiomas, e a anterior é aposentada.'),
    header: t('Publicar versão'),
    onAccept: () => {
      void runActivate()
    },
  })
}

async function runActivate(): Promise<void> {
  if (editingId.value == null) return

  isSaving.value = true
  errorCode.value = null

  try {
    const row = await activateTerm(editingId.value)
    isActive.value = row.ativo ?? false
    eventBus.emit('subscription_term.saved', { id: row.id })
    closeModal()
  } catch (err) {
    errorCode.value = err instanceof Error ? err.message : String(err)
  } finally {
    isSaving.value = false
  }
}

function handleDelete(): void {
  if (editingId.value == null) return

  confirmAction({
    message: t('Excluir esta versão e todos os seus idiomas? Só é possível enquanto ninguém a assinou.'),
    header: t('Excluir versão'),
    onAccept: () => {
      void runDelete()
    },
  })
}

async function runDelete(): Promise<void> {
  const id = editingId.value
  if (id == null) return

  isSaving.value = true
  errorCode.value = null

  try {
    await deleteTerm(id)
    eventBus.emit('subscription_term.saved', { id })
    closeModal()
  } catch (err) {
    errorCode.value = err instanceof Error ? err.message : String(err)
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.subscription-term.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.subscription-term.open', openModal)
})
</script>

<style scoped>
.term-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  color: var(--p-red-500);
  font-size: 13px;
}

.term-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--p-content-border-color, #e5e7eb);
}

.term-live {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--p-green-600, #16a34a);
  font-size: 13px;
}

.term-hint {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 12px;
}
</style>
